import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import isNil from 'lodash/isNil';
import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {AssetMachine} from '../../interfaces/AssetMachine';
import reduxSelectors from '../../redux/selectors';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'name'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'sx'
> & {
  value?: number | null;
  nullLabel?: string;
  assetMachines?: AssetMachine[];
  onChange?: (value?: number | null, item?: AssetMachine | null) => void;
};

const AssetMachineSelect = ({
  value,
  disabled,
  fullWidth,
  size,
  nullLabel,
  assetMachines,
  onChange,
  ...props
}: Props) => {
  const assets = useSelector(reduxSelectors.assets.getAssets);
  const nullValue = -1;

  const options = useMemo(() => {
    if (nullLabel) {
      return [
        {
          id: nullValue,
          name: nullLabel,
        },
        ...(assetMachines ?? assets.asset_machines),
      ];
    }
    return assetMachines ?? assets.asset_machines;
  }, [assets.asset_machines, nullLabel, assetMachines]);

  const selectedOptionId = isNil(value) ? nullValue : value;
  const selectedOption = options.find((i) => i.id === selectedOptionId) ?? null;

  const getOptionName = (item: AssetMachine) => {
    if (item.name) {
      return item.name;
    }
  };

  return disabled ? (
    <TextField
      {...props}
      value={selectedOption?.name ?? ''}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
    />
  ) : (
    <Autocomplete
      value={selectedOption ?? null}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      size={size}
      disableClearable={selectedOptionId === nullValue}
      isOptionEqualToValue={(o, v) => !!v && o.id === v.id}
      getOptionLabel={(o) => o.name ?? ''}
      renderInput={(params) => (
        <TextField label="Machine" {...props} {...params} />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.name ?? getOptionName(option as any)}
          </li>
        );
      }}
      onChange={(_, option) => {
        const v =
          !option || isNil(option.id) || option.id === nullValue
            ? null
            : option.id;
        const item = v
          ? assets.asset_machines.find((i) => i.id === v) ?? null
          : null;
        onChange?.(v, item);
      }}
    />
  );
};

export default AssetMachineSelect;
